/* ============================================================
   Scroll Reveal — observes sections and adds .sr-visible on enter
   Reads trigger threshold from data-sr-threshold on <html>.
   Staggers direct children of each section content wrapper.
   ============================================================ */
(function () {
  const docEl = document.documentElement;

  if (!docEl.classList.contains('sr-enabled')) return;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) {
    document.querySelectorAll('.sr-target').forEach((el) => el.classList.add('sr-visible'));
    return;
  }

  const threshold = parseFloat(docEl.dataset.srThreshold || '0.15');
  const staggerMs = parseInt(docEl.dataset.srStagger || '100', 10);

  // Find the meaningful content wrapper inside a Shopify section.
  // Pitch's generic section.liquid renders: .section > .custom-section-content > .section-content-wrapper
  // Custom sections (hero, app-showcase, story-feature) wrap content in their own element.
  function findContentHost(sectionEl) {
    // 1) Pitch generic section wrapper
    const generic = sectionEl.querySelector('.section-content-wrapper');
    if (generic) return generic;

    // 2) hero has .hero__content or .hero__container
    const heroContent = sectionEl.querySelector('.hero__content');
    if (heroContent) return heroContent;

    // 3) story-feature / story-timeline / app-showcase — return null,
    //    we'll reveal the whole section as a block instead.
    return null;
  }

  function shouldSkipChild(node) {
    if (!(node instanceof HTMLElement)) return true;
    const cs = getComputedStyle(node);
    if (cs.position === 'absolute' || cs.position === 'fixed') return true;
    if (cs.display === 'none' || cs.visibility === 'hidden') return true;
    if (node.getAttribute('aria-hidden') === 'true') return true;
    // Skip tiny decorative elements (icons, dots, spacers)
    if (node.offsetHeight < 20 && node.offsetWidth < 20) return true;
    return false;
  }

  function tagChildren(host, parent) {
    if (host.dataset.srTagged === '1') return;
    host.dataset.srTagged = '1';

    const kids = Array.from(host.children).filter((node) => !shouldSkipChild(node));

    if (kids.length === 0) {
      // Fall back to the parent section wrapper itself
      parent.classList.add('sr-target');
      return;
    }

    kids.forEach((node, i) => {
      node.classList.add('sr-target');
      const delay = Math.min(i * staggerMs, 600);
      node.style.setProperty('--sr-delay', delay + 'ms');
    });
  }

  function processSection(sectionEl) {
    if (sectionEl.dataset.srProcessed === '1') return;
    sectionEl.dataset.srProcessed = '1';

    // Skip if the section itself is hidden
    if (sectionEl.offsetHeight === 0 && sectionEl.offsetWidth === 0) return;

    // Skip hero-scroll-reveal (its own animation system)
    if (sectionEl.querySelector('[data-hero-reveal]')) return;

    const host = findContentHost(sectionEl);

    if (host) {
      tagChildren(host, sectionEl);
    } else {
      // story-feature / story-timeline / app-showcase / etc — reveal as a block
      sectionEl.classList.add('sr-target');
    }

    sectionEl.querySelectorAll('.sr-target').forEach((el) => io.observe(el));
  }

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('sr-visible');
        io.unobserve(entry.target);
      });
    },
    { threshold, rootMargin: '0px 0px -5% 0px' }
  );

  function setupScope(scope) {
    const sections = scope.matches('.shopify-section[id^="shopify-section-"]')
      ? [scope]
      : scope.querySelectorAll('.shopify-section[id^="shopify-section-"]');

    sections.forEach((sectionEl) => {
      // Skip header / footer section groups
      if (sectionEl.closest('#header-group, #footer-group')) return;
      processSection(sectionEl);
    });
  }

  // Initial pass — wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setupScope(document));
  } else {
    setupScope(document);
  }

  // Shopify theme editor — re-process when sections are added/removed/reordered
  if (docEl.classList.contains('shopify-design-mode')) {
    document.addEventListener('shopify:section:load', (e) => {
      const sec = e.target;
      if (sec instanceof HTMLElement) setupScope(sec);
    });
    document.addEventListener('shopify:section:select', (e) => {
      const sec = e.target;
      if (sec instanceof HTMLElement) {
        sec.querySelectorAll('.sr-target').forEach((el) => el.classList.add('sr-visible'));
      }
    });
  }
})();
